import React, { useState, useEffect, useCallback } from 'react';
import Cookies from 'js-cookie';

import moment from 'moment';
import AddIcon from '@mui/icons-material/Add';
import DescriptionIcon from '@mui/icons-material/Description';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';

const API_BASE_URL = 'https://system.sales-management.cloud/api';
const INVOICES_API = `${API_BASE_URL}/invoices.php`;

function Invoice() {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);
  const [formData, setFormData] = useState({
    period: moment().format('YYYY-MM-DD'),
    all_amount: '',
    expenses: [],
    service_fee: '',
    is_service_checked: false,
    labor_cost: '',
    company_sales: '',
    billing_ratio: '',
    amount: '',
    notes: ''
  });

  // 認証データ取得
  const getAuthData = useCallback(() => {
    const token = Cookies.get('auth_token');
    const userInfoStr = Cookies.get('user_info');
    
    if (!token || !userInfoStr) {
      return null;
    }
    
    try {
      const userInfo = JSON.parse(userInfoStr);
      return { token, user: userInfo };
    } catch (error) {
      console.error('Cookie parse error:', error);
      return null;
    }
  }, []);

  // 請求書一覧取得
  const fetchInvoices = useCallback(async () => {
    const auth = getAuthData();
    if (!auth) {
      setError('ログインが必要です');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${INVOICES_API}?limit=50`, {
        headers: {
          'Authorization': `Bearer ${auth.token}`,
        },
      });

      const result = await response.json();


      if (result.status === 'success') {
        setInvoices(result.data.invoices || []);
      } else {
        setError(result.message || '請求書の取得に失敗しました');
      }
    } catch (err) {
      console.error('Fetch error:', err);
      setError('通信エラーが発生しました');
    } finally {
      setLoading(false);
    }
  }, [getAuthData]);

  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  // 請求書作成
  const handleCreate = async () => {
    const auth = getAuthData();
    if (!auth) {
      setError('ログインが必要です');
      return;
    }

    setModalLoading(true);
    setError('');
    setSuccess('');

    try {
      const requestBody = {
        period: formData.period,
        amount: parseInt(formData.amount),
        status: 'submitted'
      };

      if (formData.notes) {
        requestBody.notes = formData.notes;
      }

      const response = await fetch(INVOICES_API, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${auth.token}`,
        },
        body: JSON.stringify(requestBody),
      });

      const result = await response.json();


      if (result.status === 'success') {
        setSuccess('請求書を提出しました');
        setIsModalOpen(false);
        setFormData({
          period: moment().format('YYYY-MM-DD'),
          all_amount: '',
          expenses: [],
          service_fee: '',
          is_service_checked: false,
          labor_cost: '',
          company_sales: '',
          billing_ratio: '',
          amount: '',
          notes: ''
        });
        fetchInvoices();
      } else {
        setError(result.message || '請求書の提出に失敗しました');
      }
    } catch (err) {
      console.error('Create error:', err);
      setError('通信エラーが発生しました');
    } finally {
      setModalLoading(false);
    }
  };

  // 請求書更新
  const handleUpdate = async () => {
    const auth = getAuthData();
    if (!auth || !selectedInvoice) {
      setError('ログインが必要です');
      return;
    }

    setModalLoading(true);
    setError('');
    setSuccess('');

    try {
      const requestBody = {
        id: selectedInvoice.invoice_id,
        amount: parseInt(formData.amount),
        notes: formData.notes,
        status: "resubmitted"
      };

      const response = await fetch(INVOICES_API, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${auth.token}`,
        },
        body: JSON.stringify(requestBody),
      });

      const result = await response.json();


      if (result.status === 'success') {
        setSuccess('請求書を更新しました');
        setIsModalOpen(false);
        setSelectedInvoice(null);
        fetchInvoices();
      } else {
        setError(result.message || '請求書の更新に失敗しました');
      }
    } catch (err) {
      console.error('Update error:', err);
      setError('通信エラーが発生しました');
    } finally {
      setModalLoading(false);
    }
  };

  // 請求書削除
  const handleDelete = async (invoiceId) => {
    if (!window.confirm('本当に削除しますか？')) {
      return;
    }

    const auth = getAuthData();
    if (!auth) {
      setError('ログインが必要です');
      return;
    }

    setError('');
    setSuccess('');

    try {
      const response = await fetch(`${INVOICES_API}?id=${invoiceId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${auth.token}`,
        },
      });

      const result = await response.json();


      if (result.status === 'success') {
        setSuccess('請求書を削除しました');
        fetchInvoices();
      } else {
        setError(result.message || '請求書の削除に失敗しました');
      }
    } catch (err) {
      console.error('Delete error:', err);
      setError('通信エラーが発生しました');
    }
  };

  // モーダルを開く
  const openCreateModal = () => {
    setSelectedInvoice(null);
    setFormData({
      period: moment().format('YYYY-MM-DD'),
      all_amount: '',
      expenses: [],
      service_fee: '',
      is_service_checked: false,
      labor_cost: '',
      company_sales: '',
      billing_ratio: '',
      amount: '',
      notes: ''
    });
    setIsModalOpen(true);
  };

  const openEditModal = (invoice) => {
    setSelectedInvoice(invoice);
    setFormData({
      period: invoice.period,
      all_amount: invoice.all_amount?.toString() || '',
      expenses: Array.isArray(invoice.expenses) ? invoice.expenses : [],
      service_fee: invoice.service_fee?.toString() || '',
      is_service_checked: invoice.is_service_checked || false,
      labor_cost: invoice.labor_cost?.toString() || '',
      company_sales: invoice.company_sales?.toString() || '',
      billing_ratio: invoice.billing_ratio?.toString() || '',
      amount: invoice.amount.toString(),
      notes: invoice.notes || ''
    });
    setIsModalOpen(true);
  };

  return (
    <div className="max-w-7xl mx-auto p-4 md:p-6">
      <Header onOpenModal={openCreateModal} />
      
      {error && <ErrorMessage message={error} />}
      {success && <SuccessMessage message={success} />}

      {loading ? (
        <LoadingSpinner />
      ) : (
        <InvoiceList
          invoices={invoices}
          onEdit={openEditModal}
          onDelete={handleDelete}
        />
      )}

      {isModalOpen && (
        <InvoiceModal
          isOpen={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
            setSelectedInvoice(null);
          }}
          formData={formData}
          setFormData={setFormData}
          onSubmit={selectedInvoice ? handleUpdate : handleCreate}
          loading={modalLoading}
          isEdit={!!selectedInvoice}
        />
      )}
    </div>
  );
}

// ヘッダー
const Header = ({ onOpenModal }) => (
  <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
    <h1 className="text-2xl md:text-3xl font-bold text-gray-900">請求書管理</h1>
    <button
      onClick={onOpenModal}
      className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm md:text-base"
    >
      <AddIcon />
      新規請求書
    </button>
  </div>
);

// エラーメッセージ
const ErrorMessage = ({ message }) => (
  <div className="mb-4 p-3 md:p-4 bg-red-50 border border-red-200 rounded-md">
    <p className="text-xs md:text-sm text-red-600">{message}</p>
  </div>
);

// 成功メッセージ
const SuccessMessage = ({ message }) => (
  <div className="mb-4 p-3 md:p-4 bg-green-50 border border-green-200 rounded-md">
    <p className="text-xs md:text-sm text-green-600">{message}</p>
  </div>
);

// ローディング
const LoadingSpinner = () => (
  <div className="flex justify-center items-center min-h-64">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
  </div>
);

// 請求書リスト
const InvoiceList = ({ invoices, onEdit, onDelete }) => {
  if (invoices.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
        請求書がありません
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-3 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                期間
              </th>
              <th className="px-3 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                請求金額
              </th>
              <th className="px-3 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                備考
              </th>
              <th className="px-3 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                ステータス
              </th>
              <th className="px-3 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                作成日
              </th>
              <th className="px-3 md:px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                操作
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {invoices.map((invoice) => (
              <InvoiceRow
                key={invoice.invoice_id}
                invoice={invoice}
                onEdit={onEdit}
                onDelete={onDelete}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// 請求書行
const InvoiceRow = ({ invoice, onEdit, onDelete }) => {
  const canEdit = invoice.status === 'draft' || invoice.status === 'returned';
  const canDelete = invoice.status === 'draft';

  return (
    <tr className="hover:bg-gray-50">
      <td className="px-3 md:px-6 py-4 whitespace-nowrap text-xs md:text-sm text-gray-900">
        {moment(invoice.period).format('YYYY/MM/DD')}
      </td>
      <td className="px-3 md:px-6 py-4 whitespace-nowrap text-xs md:text-sm font-semibold text-gray-900">
        ¥{invoice.amount?.toLocaleString() || 0}
      </td>
      <td className="px-3 md:px-6 py-4 whitespace-nowrap text-xs md:text-sm text-gray-900">
        {invoice.notes || '-'}
      </td>
      <td className="px-3 md:px-6 py-4 whitespace-nowrap">
        <StatusBadge status={invoice.status} />
      </td>
      <td className="px-3 md:px-6 py-4 whitespace-nowrap text-xs md:text-sm text-gray-500">
        {invoice.created_at ? moment(invoice.created_at).format('YYYY/MM/DD HH:mm') : '-'}
      </td>
      <td className="px-3 md:px-6 py-4 whitespace-nowrap text-right text-xs md:text-sm font-medium">
        <div className="flex justify-end gap-2">
          {canEdit && (
            <button
              onClick={() => onEdit(invoice)}
              className="text-blue-600 hover:text-blue-900 text-xs md:text-sm"
            >
              編集
            </button>
          )}
          {canDelete && (
            <button
              onClick={() => onDelete(invoice.invoice_id)}
              className="text-red-600 hover:text-red-900 text-xs md:text-sm"
            >
              削除
            </button>
          )}
        </div>
      </td>
    </tr>
  );
};

// ステータスバッジ
const StatusBadge = ({ status }) => {
  const statusConfig = {
    draft: { label: '下書き', color: 'bg-gray-100 text-gray-800', icon: <DescriptionIcon fontSize="small" /> },
    submitted: { label: '提出済み', color: 'bg-blue-100 text-blue-800', icon: <HourglassEmptyIcon fontSize="small" /> },
    resubmitted: { label: '再提出済み', color: 'bg-blue-100 text-blue-800', icon: <HourglassEmptyIcon fontSize="small" /> },
    approved: { label: '承認済み', color: 'bg-green-100 text-green-800', icon: <CheckCircleIcon fontSize="small" /> },
    paid: { label: '支払済み', color: 'bg-green-100 text-green-800', icon: <CheckCircleIcon fontSize="small" /> },
    rejected: { label: '却下', color: 'bg-red-100 text-red-800', icon: <ErrorIcon fontSize="small" /> },
    returned: { label: '差戻し', color: 'bg-yellow-100 text-yellow-800', icon: <ErrorIcon fontSize="small" /> },
  };

  const config = statusConfig[status] || statusConfig.draft;

  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
      {config.label}
    </span>
  );
};

// 請求書モーダル
const InvoiceModal = ({ isOpen, onClose, formData, setFormData, onSubmit, loading, isEdit }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="p-4 md:p-6">
          <h2 className="text-lg md:text-xl font-bold text-gray-900 mb-4">
            {isEdit ? '請求書編集' : '請求書作成'}
          </h2>

          <div className="space-y-4">
            {!isEdit && (
              <div>
                <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1">
                  着金日 <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={formData.period}
                  onChange={(e) => setFormData({ ...formData, period: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 text-sm"
                  disabled={isEdit}
                />
              </div>
            )}

            <div>
              <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1">
                契約金額 <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                inputMode="numeric"
                value={formData.all_amount ? Number(formData.all_amount).toLocaleString() : ''}
                onChange={(e) => {
                  const numericValue = e.target.value.replace(/[^0-9]/g, '');
                  const allAmount = numericValue === '' ? 0 : parseInt(numericValue, 10);
                  const serviceFee = parseFloat(formData.service_fee) || 0;
                  const totalExpenses = formData.expenses.reduce((sum, val) => sum + val, 0) + (formData.is_service_checked ? serviceFee : 0);
                  const laborCost = parseFloat(formData.labor_cost) || 0;
                  const companySales = Math.round((allAmount - totalExpenses - laborCost) * 0.8);
                  setFormData({ 
                    ...formData, 
                    all_amount: numericValue,
                    company_sales: companySales.toString()
                  });
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 text-sm"
                placeholder="例: 2500000"
              />
            </div>

            <div>
              <label className="block text-xs md:text-sm font-medium text-gray-700 mb-2">
              諸経費<span className="text-red-500">*</span>
              </label>
              <div className="space-y-2 p-3 border border-gray-300 rounded-md">
                {[
                  { value: 0, label: '諸経費なし - 0円', key: 'none' },
                  { value: 264000, label: 'Set - 264,000円', key: 'set' },
                  { value: 165000, label: '保証のみ - 165,000円', key: 'warranty' },
                  { value: 110000, label: 'トルネオ - 110,000円', key: 'torneo' },
                ].map((expense) => (
                  <label key={expense.key} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.expenses.includes(expense.value)}
                      onChange={(e) => {
                        const allAmount = parseFloat(formData.all_amount) || 0;
                        const laborCost = parseFloat(formData.labor_cost) || 0;
                        let newExpenses;
                        
                        if (e.target.checked) {
                          newExpenses = [...formData.expenses, expense.value];
                        } else {
                          newExpenses = formData.expenses.filter(v => v !== expense.value);
                        }
                        
                        const serviceFee = parseFloat(formData.service_fee) || 0;
                        const totalExpenses = newExpenses.reduce((sum, val) => sum + val, 0) + (formData.is_service_checked ? serviceFee : 0);
                        const companySales = Math.round((allAmount - totalExpenses - laborCost) * 0.8);
                        
                        setFormData({ 
                          ...formData, 
                          expenses: newExpenses,
                          company_sales: companySales.toString()
                        });
                      }}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">{expense.label}</span>
                  </label>
                ))}
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.is_service_checked}
                    onChange={(e) => {
                      const allAmount = parseFloat(formData.all_amount) || 0;
                      const laborCost = parseFloat(formData.labor_cost) || 0;
                      const serviceFee = parseFloat(formData.service_fee) || 0;
                      const totalExpenses = formData.expenses.reduce((sum, val) => sum + val, 0) + (e.target.checked ? serviceFee : 0);
                      const companySales = Math.round((allAmount - totalExpenses - laborCost) * 0.8);
                      
                      setFormData({ 
                        ...formData, 
                        is_service_checked: e.target.checked,
                        company_sales: companySales.toString()
                      });
                    }}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">サービス - 任意料金</span>
                </label>
                {formData.is_service_checked && (
                  <div className="ml-6 mt-2">
                    <input
                      type="text"
                      inputMode="numeric"
                      value={formData.service_fee ? Number(formData.service_fee).toLocaleString() : ''}
                      onChange={(e) => {
                        const numericValue = e.target.value.replace(/[^0-9]/g, '');
                        const allAmount = parseFloat(formData.all_amount) || 0;
                        const laborCost = parseFloat(formData.labor_cost) || 0;
                        const serviceFee = numericValue === '' ? 0 : parseInt(numericValue, 10);
                        const totalExpenses = formData.expenses.reduce((sum, val) => sum + val, 0) + serviceFee;
                        const companySales = Math.round((allAmount - totalExpenses - laborCost) * 0.8);
                        
                        setFormData({ 
                          ...formData, 
                          service_fee: numericValue,
                          company_sales: companySales.toString()
                        });
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 text-sm"
                      placeholder="サービス料金を入力"
                    />
                  </div>
                )}
              </div>
              <p className="mt-1 text-xs text-gray-500">
                合計: ¥{(formData.expenses.reduce((sum, val) => sum + val, 0) + (formData.is_service_checked ? (parseFloat(formData.service_fee) || 0) : 0)).toLocaleString()}
              </p>
            </div>

            <div>
              <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1">
              工賃<span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                inputMode="numeric"
                value={formData.labor_cost ? Number(formData.labor_cost).toLocaleString() : ''}
                onChange={(e) => {
                  const numericValue = e.target.value.replace(/[^0-9]/g, '');
                  const allAmount = parseFloat(formData.all_amount) || 0;
                  const serviceFee = parseFloat(formData.service_fee) || 0;
                  const totalExpenses = formData.expenses.reduce((sum, val) => sum + val, 0) + (formData.is_service_checked ? serviceFee : 0);
                  const laborCost = numericValue === '' ? 0 : parseInt(numericValue, 10);
                  const companySales = Math.round((allAmount - totalExpenses - laborCost) * 0.8);
                  setFormData({ 
                    ...formData, 
                    labor_cost: numericValue,
                    company_sales: companySales.toString()
                  });
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 text-sm"
                placeholder="例: 2500000"
              />
            </div>

            <div>
              <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1">
              会社売上(80%)<span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.company_sales ? Number(formData.company_sales).toLocaleString() : ''}
                className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-sm"
                placeholder="自動計算"
                readOnly
              />
            </div>

            <div>
              <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1">
              請求割合<span className="text-red-500">*</span>
              </label>
              <select
                value={formData.billing_ratio}
                onChange={(e) => {
                  const ratio = parseFloat(e.target.value) || 0;
                  const companySales = parseFloat(formData.company_sales) || 0;
                  const amount = Math.round(companySales * ratio);
                  setFormData({ 
                    ...formData, 
                    billing_ratio: e.target.value,
                    amount: amount.toString()
                  });
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 text-sm"
              >
                <option value="">選択してください</option>
                <option value="0.25">25%</option>
                <option value="0.30">30%</option>
                <option value="0.50">50%</option>
                <option value="0.60">60%</option>
              </select>
            </div>

            <div>
              <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1">
                請求金額 <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.amount ? Number(formData.amount).toLocaleString() : ''}
                className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-sm"
                placeholder="自動計算"
                readOnly
              />
            </div>

            <div>
              <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1">
                備考
              </label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 text-sm"
                placeholder={`〇〇邸の保証のみ契約分\nサービス：カートリッジ2本含む`}
              />
            </div>
          </div>

          <div className="mt-6 flex gap-3 justify-end">
            <button
              onClick={onClose}
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 disabled:opacity-50"
            >
              キャンセル
            </button>
            <button
              onClick={onSubmit}
              disabled={loading || !formData.amount}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
            >
              {loading ? '処理中...' : isEdit ? '更新' : '提出'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Invoice;